import {
  add,
  allocate,
  applyRate,
  type BasisPoints,
  DEFAULT_ROUNDING,
  inclusivePortion,
  type Money,
  multiplyByQuantity,
  pow10,
  type Quantity,
  type RoundingMode,
  subtract,
  sum,
  zero,
} from "@tovi/shared/money";
import { campaignDiscountsByLine } from "./campaigns.ts";
import type {
  OrderLine,
  PricedLine,
  PricedOrder,
  PricingInput,
  PricingSettings,
  TaxLine,
  TaxRule,
} from "./types.ts";

/**
 * Thrown for a configuration the engine could only handle by guessing at
 * arithmetic. Refusing is deliberate: a wrong answer here is money taken from
 * a real customer, and it would be silent.
 */
export class UnsupportedPricingError extends Error {
  override readonly name = "UnsupportedPricingError";
}

/** A quantity beyond this on one line is a data error, not a big table. */
const MAX_UNITS_PER_LINE = 100_000n;

/**
 * The pricing pipeline of `03-data-model.md` § 5.2, in the order the spec
 * writes it: per-line gross, discounts, order-level charges, tax by sequence,
 * total.
 *
 * Pure — no clock, no database, no locale. Everything it needs is in the input,
 * which is also what makes every branch a fixture rather than a new test.
 */
export function priceOrder(input: PricingInput): PricedOrder {
  const { currency } = input;
  const settings = input.settings ?? {};

  if (settings.includeTaxInWholeDiscount) {
    throw new UnsupportedPricingError(
      "includeTaxInWholeDiscount: only the before-tax branch of the whole-bill " +
        "discount is specified (03-data-model.md § 5.2). The after-tax branch " +
        "changes the money and has no evidence behind it yet.",
    );
  }

  const lineMode = settings.lineRounding ?? DEFAULT_ROUNDING;
  const nothing = zero(currency);

  // PER LINE — unit_price + Σ modifiers + packing fee, then quantity.
  const isTakeaway = input.diningStyle === "take_away";
  const grosses = input.lines.map((line) => {
    let unit = line.unitPrice;
    for (const modifier of line.modifiers ?? []) unit = add(unit, modifier);
    if (isTakeaway && line.packingFee) unit = add(unit, line.packingFee);
    return { unitGross: unit, gross: multiplyByQuantity(unit, line.quantity, lineMode) };
  });

  const itemDiscounts = input.lines.map((line) => line.itemDiscount ?? nothing);

  // PROMOTIONAL CAMPAIGNS — every Nth item off, cheapest first
  // (`MARKETING_PROMOTIONAL_CAMPAIGN`). Between the item discount and the
  // whole-bill one, because the source's own service-fee copy puts the charge
  // "after promotional and item discounts" and the bill discount is prorated
  // over what each line still carries.
  const promoDiscounts = campaignDiscountsByLine(
    input.lines,
    input.campaigns ?? [],
    currency,
    lineMode,
  );

  const afterItemDiscount = grosses.map((g, i) => {
    const afterItem = subtract(g.gross, itemDiscounts[i] ?? nothing);
    const promo = promoDiscounts[i] ?? nothing;
    // A campaign can never take a line below nothing, whatever else came off
    // it first: two offers on one dish must not pay the customer.
    return promo.amount > afterItem.amount ? nothing : subtract(afterItem, promo);
  });

  // The whole-bill discount is prorated by what each line still carries, and
  // skips whitelisted items entirely. `allocate` distributes the rounding
  // remainder so the shares sum back to the discount exactly.
  const billShares = prorateBillDiscount(input, afterItemDiscount, currency);

  const nets = afterItemDiscount.map((m, i) => subtract(m, billShares[i] ?? nothing));

  // ORDER-LEVEL CHARGES — the service charge is scoped by dining style the same
  // way a tax rule is, and for the same reason: which styles carry one is a
  // merchant's policy, not something the engine should decide.
  const chargeScope = settings.serviceChargeDiningStyleScope;
  const serviceChargeApplies =
    !chargeScope || chargeScope.length === 0 || chargeScope.includes(input.diningStyle);
  const lineServiceCharges = serviceChargeApplies
    ? serviceChargesOf(input.lines, nets, input, settings, currency)
    : input.lines.map(() => nothing);

  const serviceCharge = sum(lineServiceCharges, currency);
  const tableFee = input.tableFee ?? nothing;
  const subtotal = sum(nets, currency);

  // TAX — resolved per rule, in sequence order.
  const taxLines = taxLinesOf(input, settings, nets, serviceCharge, tableFee, currency);

  const inclusiveTaxTotal = sum(
    taxLines.filter((t) => t.isInclusiveSnapshot).map((t) => t.amount),
    currency,
  );
  const exclusiveTaxTotal = sum(
    taxLines.filter((t) => !t.isInclusiveSnapshot).map((t) => t.amount),
    currency,
  );

  // An inclusive tax is already inside `subtotal`. Adding it here is the single
  // most likely bug in this product (§ 5.2), so only the exclusive total lands.
  const total = add(add(add(subtotal, serviceCharge), tableFee), exclusiveTaxTotal);

  // The tip lands *after* tax and touches nothing above it. That ordering is
  // the whole safeguard: `taxLinesOf` is called with the nets, the service
  // charge and the table fee, and there is no argument it could arrive
  // through — so a voluntary gratuity cannot be taxed by any configuration of
  // rules, including one a merchant writes by hand. Making it a flag would
  // have made it a mistake somebody could make.
  const tip = input.tip ?? nothing;
  if (tip.amount < 0n) {
    throw new UnsupportedPricingError(
      "tip: a gratuity cannot be negative. Giving money back is a refund, " +
        "which is a different instrument with its own audit trail.",
    );
  }
  const amountDue = add(total, tip);

  const lines: PricedLine[] = input.lines.map((line, i) => ({
    id: line.id,
    ...(line.categoryId === undefined ? {} : { categoryId: line.categoryId }),
    unitGross: grosses[i]?.unitGross ?? nothing,
    gross: grosses[i]?.gross ?? nothing,
    itemDiscount: itemDiscounts[i] ?? nothing,
    promoDiscount: promoDiscounts[i] ?? nothing,
    billShare: billShares[i] ?? nothing,
    net: nets[i] ?? nothing,
    serviceCharge: lineServiceCharges[i] ?? nothing,
  }));

  return {
    lines,
    subtotal,
    serviceCharge,
    tableFee,
    taxLines,
    inclusiveTaxTotal,
    exclusiveTaxTotal,
    total,
    tip,
    amountDue,
  };
}

/**
 * Spreads a whole-bill discount across the lines that accept one, in
 * proportion to what each still carries after its own item discount.
 */
function prorateBillDiscount(
  input: PricingInput,
  afterItemDiscount: readonly Money[],
  currency: Money["currency"],
): Money[] {
  const nothing = zero(currency);
  const discount = input.billDiscount;
  if (!discount || discount.amount === 0n) return input.lines.map(() => nothing);

  const weights = afterItemDiscount.map((m, i) =>
    input.lines[i]?.excludedFromBillDiscount ? 0n : m.amount > 0n ? m.amount : 0n,
  );

  // Every eligible line is whitelisted or worth nothing. `allocate` would put
  // the whole discount on the first line to preserve its sum invariant, which
  // here would mean discounting an item the merchant excluded on purpose.
  if (weights.every((w) => w === 0n)) return input.lines.map(() => nothing);

  return allocate(discount, weights);
}

/**
 * The service charge, per line.
 *
 * `on_subtotal` is the rule as `03-data-model.md` § 5.2 writes it: one rate
 * against the sum of eligible nets, rounded once. Lines are grouped by rate
 * first, because a per-item override means one bill can carry several.
 *
 * `per_unit` applies the rate to each unit and rounds each one. That is what
 * BACARO's till does — it reproduces 9 of 9 captured orders where the subtotal
 * rule reproduces 3 — and it is a store setting rather than a constant, because
 * one market's till behaviour is not a universal rule.
 */
function serviceChargesOf(
  lines: readonly OrderLine[],
  nets: readonly Money[],
  input: PricingInput,
  settings: PricingSettings,
  currency: Money["currency"],
): Money[] {
  const nothing = zero(currency);
  const mode = settings.serviceChargeRounding ?? DEFAULT_ROUNDING;
  const calculation = settings.serviceChargeCalculation ?? "on_subtotal";

  const rateOf = (line: OrderLine): BasisPoints =>
    line.excludedFromServiceCharge
      ? 0
      : (line.serviceChargeRateBp ?? input.serviceChargeRateBp ?? 0);

  if (calculation === "per_unit") {
    return lines.map((line, i) => {
      const rate = rateOf(line);
      const net = nets[i] ?? nothing;
      if (rate === 0 || net.amount === 0n) return nothing;
      const shares = unitSharesOf(net, line.quantity);
      return sum(
        shares.map((share) => applyRate(share, rate, mode)),
        currency,
      );
    });
  }

  // Group by rate, charge each group once, then hand the group's charge back to
  // its lines in proportion to their nets so a per-line figure still exists.
  const charges: Money[] = lines.map(() => nothing);
  const groups = new Map<BasisPoints, number[]>();
  lines.forEach((line, i) => {
    const rate = rateOf(line);
    if (rate === 0) return;
    const bucket = groups.get(rate);
    if (bucket) bucket.push(i);
    else groups.set(rate, [i]);
  });

  for (const [rate, indices] of groups) {
    const groupNets = indices.map((i) => nets[i] ?? nothing);
    const groupCharge = applyRate(sum(groupNets, currency), rate, mode);
    if (groupCharge.amount === 0n) continue;
    const weights = groupNets.map((m) => (m.amount > 0n ? m.amount : 0n));
    const shares = weights.every((w) => w === 0n)
      ? indices.map(() => nothing)
      : allocate(groupCharge, weights);
    indices.forEach((lineIndex, k) => {
      charges[lineIndex] = shares[k] ?? nothing;
    });
  }

  return charges;
}

/**
 * A line's net split into one share per unit, so a per-unit charge can be
 * rounded per unit. Splitting the net (rather than re-deriving the unit price)
 * is what keeps discounts inside the per-unit base.
 *
 * A fractional quantity has no "unit" to charge — a weighed item sells 1.250 kg
 * — so it falls back to a single share, i.e. one rounding for the line.
 *
 * "Fractional" is a question about the value, not about the scale: two burgers
 * reach this function as `{ 2n, 0 }` from the till's cart and as `{ 2000n, 3 }`
 * from `pos_order_lines`. Both are two units. Testing the scale instead made
 * this branch dead on the server, which showed the cashier one total and
 * settled the customer at another.
 */
function unitSharesOf(net: Money, quantity: Quantity): Money[] {
  const perUnit = pow10(quantity.scale);
  if (quantity.value % perUnit !== 0n) return [net];
  const units = quantity.value / perUnit;
  if (units <= 1n) return [net];
  if (units > MAX_UNITS_PER_LINE) {
    throw new UnsupportedPricingError(
      `A line of ${units} units exceeds the ${MAX_UNITS_PER_LINE} per-unit limit`,
    );
  }
  return allocate(
    net,
    Array.from({ length: Number(units) }, () => 1n),
  );
}

/**
 * Resolves the applicable rules for this order and computes one tax line each,
 * in `sequence` order, per the resolver in `03-data-model.md` § 5.1.
 */
function taxLinesOf(
  input: PricingInput,
  settings: PricingSettings,
  nets: readonly Money[],
  serviceCharge: Money,
  tableFee: Money,
  currency: Money["currency"],
): TaxLine[] {
  const nothing = zero(currency);
  const chargeTax = settings.chargeTax ?? true;
  if (!chargeTax || input.isTaxExempt) return [];

  const mode: RoundingMode = settings.taxRounding ?? DEFAULT_ROUNDING;
  const rules = (input.taxRules ?? [])
    .filter((rule) => matchesDiningStyle(rule, input))
    .slice()
    .sort((a, b) => a.sequence - b.sequence);

  // An inclusive rule's amount is a share of the net, and a compound rule's
  // base is the net plus what has been added to it. A rule claiming both would
  // want the inclusive portion of a base that already carries a tax, which is
  // not a defined quantity — so it gets the refusal `includeTaxInWholeDiscount`
  // gets, for the same reason.
  const both = rules.find((rule) => rule.isInclusive && rule.isCompound);
  if (both) {
    throw new UnsupportedPricingError(
      `Tax rule ${both.code} is both inclusive and compound. An inclusive rate is ` +
        "already inside the net, so compounding it over a prior amount has no " +
        "specified meaning (03-data-model.md § 5.1).",
    );
  }

  const lines: TaxLine[] = [];
  // Exclusive amounts only. An inclusive amount is not additional money — it is
  // part of the net every base is already built from — so compounding over it
  // charges the second rule against the first rule's share of the same dollar.
  let priorAmounts = nothing;

  for (const rule of rules) {
    const taxableLines = input.lines
      .map((line, i) => ({ line, net: nets[i] ?? nothing }))
      .filter(({ line }) => !line.isTaxExempt && matchesCategory(rule, line));

    let base = sum(
      taxableLines.map(({ net }) => net),
      currency,
    );
    if (rule.appliesToServiceCharge) base = add(base, serviceCharge);
    if (rule.appliesToTableFee) base = add(base, tableFee);
    if (rule.isCompound) base = add(base, priorAmounts);

    const amount = rule.isInclusive
      ? inclusivePortion(base, rule.rateBp, mode)
      : applyRate(base, rule.rateBp, mode);

    lines.push({
      code: rule.code,
      name: rule.name,
      rateBpSnapshot: rule.rateBp,
      isInclusiveSnapshot: rule.isInclusive,
      sequence: rule.sequence,
      taxableBase: base,
      amount,
    });

    if (!rule.isInclusive) priorAmounts = add(priorAmounts, amount);
  }

  return lines;
}

function matchesDiningStyle(rule: TaxRule, input: PricingInput): boolean {
  const scope = rule.diningStyleScope;
  return !scope || scope.length === 0 || scope.includes(input.diningStyle);
}

function matchesCategory(rule: TaxRule, line: OrderLine): boolean {
  const scope = rule.categoryScope;
  if (!scope || scope.length === 0) return true;
  return line.categoryId !== undefined && scope.includes(line.categoryId);
}
